import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackLeadConversion } from '../../lib/gtag';
import { getUtm } from '../../lib/utm';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CheckCircle, X, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SideLeadPopup = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [appeared, setAppeared] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [website, setWebsite] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setOpen(true); setAppeared(true); }, 5000);
    const onOpen = () => { setOpen(true); setAppeared(true); };
    window.addEventListener('open-lead-popup', onOpen);
    return () => { clearTimeout(t); window.removeEventListener('open-lead-popup', onOpen); };
  }, []);

  const handleClose = () => {
    setOpen(false);
    if (onDone) onDone();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || name.trim().length < 2) { toast.error("Введіть ім'я"); return; }
    if (!phone.trim() || phone.trim().length < 5) { toast.error('Введіть телефон'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(),
        phone: phone.trim(),
        message: message.trim(),
        source: 'side-popup',
        website,
        ...getUtm()
      });
      trackLeadConversion();
      setSent(true);
      toast.success("Дякуємо! Менеджер зв'яжеться з вами найближчим часом", { duration: 6000 });
      setTimeout(() => {
        handleClose();
        setSent(false);
        setName(''); setPhone(''); setMessage('');
      }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Помилка надсилання');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    if (!appeared) return null;
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed left-4 bottom-20 md:bottom-4 z-[55] w-14 h-14 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 text-white flex items-center justify-center shadow-[0_0_25px_rgba(6,182,212,0.6)] hover:shadow-[0_0_40px_rgba(6,182,212,0.9)] hover:scale-110 transition-all animate-pulse-slow"
        aria-label="Залишити заявку"
        data-testid="lead-popup-minimized"
      >
        <Send className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div
      className="fixed left-4 bottom-4 z-[60] w-[calc(100vw-2rem)] max-w-sm animate-slideUpFade"
      data-testid="side-lead-popup"
    >
      <div className="relative overflow-hidden rounded-2xl border border-blue-500/40 bg-[#0a1224]/95 backdrop-blur-xl shadow-[0_0_40px_-10px_rgba(59,130,246,0.6)]">
        {/* electric glow accents */}
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-blue-500/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/60 to-transparent" />

        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 text-slate-400 hover:text-white hover:border-blue-400 transition-all flex items-center justify-center"
          aria-label="Закрити"
          data-testid="lead-popup-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-5">
          {!sent ? (
            <>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.5)] flex-shrink-0">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-base leading-tight">
                    Залиште заявку — передзвонимо за 5 хв
                  </h3>
                  <p className="text-slate-400 text-xs mt-1">
                    Безкоштовна консультація з логістом
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-2.5">
                <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0 }}
                  tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <Input
                  placeholder="Ваше ім'я"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400"
                  data-testid="lead-popup-name"
                  required
                />
                <Input
                  type="tel"
                  placeholder="+380 ..."
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="h-10 bg-slate-900/60 border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400"
                  data-testid="lead-popup-phone"
                  required
                />
                <textarea
                  placeholder="Коротко про ваш запит (необов'язково)"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm bg-slate-900/60 border border-blue-500/30 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none rounded-md resize-none"
                  data-testid="lead-popup-message"
                />
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold shadow-[0_0_20px_rgba(6,182,212,0.5)] hover:shadow-[0_0_30px_rgba(6,182,212,0.8)] transition-all"
                  data-testid="lead-popup-submit"
                >
                  {submitting ? 'Відправляю...' : (<><Send className="w-4 h-4 mr-2" />Відправити заявку</>)}
                </Button>

                <p className="text-[10px] text-slate-500 text-center mt-1">
                  Натискаючи кнопку, ви погоджуєтесь на обробку даних
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-6" data-testid="lead-popup-success">
              <div className="w-14 h-14 mx-auto bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(16,185,129,0.6)] mb-3">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <h4 className="text-white font-bold text-lg mb-1">Заявку прийнято!</h4>
              <p className="text-slate-300 text-sm">Менеджер скоро зателефонує вам</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
